import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import ChatBot from "./ChatBot";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js`;

const Course = ({ dir = "ltr" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [currentDocUrl, setCurrentDocUrl] = useState(null);
  const [completed, setCompleted] = useState(() => {
    const saved = localStorage.getItem("completedChapters");
    return saved ? JSON.parse(saved) : [];
  });
  const [showChat, setShowChat] = useState(true);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const token = localStorage.getItem("token");

  const query = new URLSearchParams(location.search);
  const id = query.keys().next().value;
  const continueNext = query.get("continue");
  const chapterFromQuery = parseInt(query.get("chapter"));

  useEffect(() => {
    localStorage.setItem("completedChapters", JSON.stringify(completed));
  }, [completed]);

  useEffect(() => {
    if (!id) return;

    const fetchCourse = async () => {
      try {
        const base_url = process.env.REACT_APP_LMS_APP_BASE_URL;
        const response = await axios.get(
          `${base_url}/user/courses/my-courses/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = response.data?.data;
        setCourse(data.course);
        setModules(data.modules || []);

        // Handle returning from quiz
        if (continueNext === "next" && !isNaN(chapterFromQuery)) {
          setCompleted((prev) => [...new Set([...prev, chapterFromQuery])]);
          setCurrentChapter(chapterFromQuery + 1);
        }
      } catch (error) {
        console.error("Error fetching course details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id, token, continueNext, chapterFromQuery]);

  const handleComplete = () => {
    const currentModule = modules[currentChapter];
    const nextChapterIndex = currentChapter + 1;

    if (!completed.includes(currentChapter)) {
      setCompleted([...completed, currentChapter]);
    }

    if (course.quizType === "sequential" && currentModule?.quiz?._id) {
      navigate(
        `/lms/quize?quizId=${currentModule.quiz._id}&courseId=${course._id}&moduleId=${currentModule._id}&quizeType=${course.quizType}&chapter=${currentChapter}`
      );
      return;
    }

    if (nextChapterIndex < modules.length) {
      setCurrentChapter(nextChapterIndex);
      setCurrentDocUrl(null);
      setPdfError(false);
    } else {
      alert("🎉 Course completed!");
    }
  };

  if (loading)
    return (
      <div className="text-center py-10 text-gray-500">Loading course...</div>
    );
  if (!course)
    return (
      <div className="text-center py-10 text-red-500">Course not found.</div>
    );

  // const currentDocs = modules[currentChapter]?.documents || [];
  const currentModuleTitle = modules[currentChapter]?.title || "No Chapter Selected";

  return (
    <div
      dir={dir}
      className={`flex relative h-full overflow-hidden ${dir === "rtl" ? "mr-16" : "ml-16"
        }`}
      style={{ height: "90vh" }}
    >
      <ChatBot dir={dir} showChat={showChat} setShowChat={setShowChat} />

      {/* Sidebar */}
      <div className="w-1/4 bg-gray-100 p-4 border-r overflow-y-auto">
        <h2 className="text-lg font-bold mb-4">{course.title}</h2>
        <ul>
          {modules.map((module, idx) => (
            <li key={module._id} className="mb-3">
              {/* Chapter title */}
               
              {/* Documents list under each chapter */}
              <div className="p-2 rounded">
                <div
                  onClick={() => {
                    setCurrentChapter(idx);
                    setCurrentDocUrl(null);
                    setPdfError(false);
                  }}
                  className={`cursor-pointer p-2 rounded ${currentChapter === idx ? "bg-red-200" : "hover:bg-gray-200"
                    }`}
                >
                  {module.title}{" "}
                  {completed.includes(idx) && (
                    <span className="text-green-600 font-semibold">(Completed)</span>
                  )}
                </div>

                {/* Documents list */}
                {currentChapter === idx && module.documents?.length > 0 && (
                  <ul className="ml-4 mt-1 border-l border-gray-300 pl-3">
                    {module.documents.map((docUrl, docIdx) => (
                      <li
                        key={docIdx}
                        onClick={() => {
                          setCurrentDocUrl(docUrl);
                          setPdfError(false);
                        }}
                        className={`cursor-pointer text-sm p-1 rounded ${currentDocUrl === docUrl
                            ? "bg-purple-200 font-semibold"
                            : "hover:bg-gray-200"
                          }`}
                      >
                        📄 Document {docIdx + 1}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Main Viewer */}
      <div className="flex-1 pl-6 pr-6 flex flex-col overflow-hidden">
        <h1 className="text-xl font-bold mb-4">{currentModuleTitle}</h1>

        <div className="border rounded-lg flex-1 bg-gray-50 p-4 overflow-y-auto">
          {!currentDocUrl ? (
            <p className="text-gray-500 text-center">
              Select a document to view.
            </p>
          ) : (
            (() => {
              const ext = currentDocUrl.split(".").pop().toLowerCase();
              const isPDF = ext === "pdf";
              const isDoc = ext === "doc" || ext === "docx";
              const isPpt = ext === "ppt" || ext === "pptx";

              if (isPDF && !pdfError) {
                return (
                  <div className="flex justify-center overflow-y-auto h-[75vh]">
                    <Document
                      file={currentDocUrl}
                      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                      onLoadError={() => setPdfError(true)}
                      className="flex flex-col items-center"
                    >
                      {Array.from(new Array(numPages), (el, index) => (
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          width={800}
                        />
                      ))}
                    </Document>
                  </div>
                );
              } else if (isPDF && pdfError) {
                return (
                  <iframe
                    src={`https://docs.google.com/gview?url=${encodeURIComponent(
                      currentDocUrl
                    )}&embedded=true`}
                    className="w-full h-[75vh] border-none overflow-hidden"
                    title="PDF Viewer"
                  />
                );
              } else if (isDoc || isPpt) {
                return (
                  <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                      currentDocUrl
                    )}`}
                    className="w-full h-[75vh] border-none overflow-hidden"
                    title="Office Document"
                  />
                );
              } else {
                return (
                  <a
                    href={currentDocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    Open Document
                  </a>
                );
              }
            })()
          )}
        </div>

        {/* Controls */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={() => {
              setCurrentChapter(Math.max(0, currentChapter - 1));
              setCurrentDocUrl(null);
            }}
            disabled={currentChapter === 0}
            className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={handleComplete}
            className="px-4 py-2 text-white rounded bg-[#B03982]"
          >
            {currentChapter === modules.length - 1
              ? "Finish Course"
              : "Complete & Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Course;
